import { useContext } from 'react';
import { ForecastFieldsContext } from '../contexts/forecastFieldsTypes';
import { hasForecastType, type ForecastField } from '../api/forecastFieldsService';

/**
 * Hook to access forecast field metadata from the ForecastFieldsContext.
 * The context fetches when the session is valid and shares data across consumers,
 * avoiding duplicate requests when components mount/remount.
 */
export function useForecastFields() {
  const context = useContext(ForecastFieldsContext);
  
  if (context === undefined) {
    throw new Error('useForecastFields must be used within a ForecastFieldsProvider');
  }

  const { fields, fieldsByDbName, loading, error, refetch } = context;

  const isHourlyField = (field: ForecastField) => hasForecastType(field, 'hourly');

  const isDailyField = (field: ForecastField) => hasForecastType(field, 'daily');

  // Legacy: Get hourly-relevant fields (for backward compatibility)
  const hourlyFields = fields.filter(f => 
    isHourlyField(f) && !f.hidden
  );

  // Legacy: Get daily-relevant fields (for backward compatibility)
  const dailyFields = fields.filter(f => 
    isDailyField(f) && !f.hidden
  );

  // Granular field lists for each view context
  const hourlyCardsFields = fields.filter(f => 
    isHourlyField(f) &&
    !f.hidden && 
    f.show_in_hourly_cards
  );

  const hourlyTableFields = fields.filter(f => 
    isHourlyField(f) &&
    !f.hidden && 
    f.show_in_hourly_table
  );

  const dailyCardsFields = fields.filter(f => 
    isDailyField(f) &&
    !f.hidden && 
    f.show_in_daily_cards
  );

  const dailyTableFields = fields.filter(f => 
    isDailyField(f) &&
    !f.hidden && 
    f.show_in_daily_table
  );

  /**
   * Get field metadata by db_name, with a fallback for unknown fields
   */
  const getField = (dbName: string): ForecastField | null => {
    return fieldsByDbName.get(dbName) || null;
  };

  /**
   * Infer unit from field name for unconfigured fields
   * Used when displaying legacy data that isn't in the forecast_fields table
   * Note: Wind fields are stored in m/s but displayed in km/h (conversion happens at component level)
   */
  const inferUnitFromName = (dbName: string): string | null => {
    const lower = dbName.toLowerCase();
    if (lower.includes('temp') || lower.includes('heat_index') || lower.includes('dew_point')) return '°C';
    if (lower.includes('pressure')) return 'hPa';
    if (lower.includes('wind') || lower.includes('gust')) return 'm/s'; // Stored unit; display conversion to km/h is in components
    if (lower.includes('humidity') || lower.includes('probability') || lower.includes('cloud_cover')) return '%';
    if (lower.includes('visibility')) return 'km';
    if (lower.includes('precipitation_intensity')) return 'mm/hr';
    return null;
  };

  /**
   * Create a default field for parameters not in the forecast_fields table
   * Infers unit from field name patterns for proper display
   */
  const createDefaultField = (dbName: string, unit?: string | null): ForecastField => ({
    api_name: dbName,
    db_name: dbName,
    display_name: dbName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    unit: unit || inferUnitFromName(dbName),
    forecast_types: ['daily', 'hourly'],
    calculated: false,
    hidden: false,
    demo_only: false,
    show_in_hourly_cards: true,
    show_in_hourly_table: true,
    show_in_daily_cards: true,
    show_in_daily_table: true,
    show_in_map_card: true,
    card_group: null,
    group_display_name: null,
    group_position: null,
    position: null,
  });

  return { 
    fields, 
    fieldsByDbName, 
    // Legacy (for backward compatibility)
    hourlyFields,
    dailyFields,
    // Granular view-specific field lists
    hourlyCardsFields,
    hourlyTableFields,
    dailyCardsFields,
    dailyTableFields,
    loading, 
    error,
    getField,
    createDefaultField,
    refetch,
  };
}
